import { useBrainStore } from '@/state/brainStore';
import { useSimulationStore } from '@/state/simulationStore';
import { eventBus } from '@/events/eventBus';
import { seedNow } from '@/domain/seedContext';
import type { NoRebookFlag } from '@/domain/types';

function nowIso(): string {
  return seedNow() ?? useSimulationStore.getState().clockIso;
}

export interface FlagNoRebookInput {
  bookingId: string;
  /** Mandatory typed reason captured on the decline path (RebookOutcome.reason). */
  reason: string;
  /** Optional free-text note from the same capture: logged, not stored on the flag. */
  reasonNote?: string;
}

/**
 * Leg (a) of the no-rebook mid-piece rule (tv-3gl, diagrams 01/07/09): raise an
 * Ops flag carrying the MANDATORY typed reason so the no-rebook is accountable and
 * someone chases the unfinished work. State only: the Ops worklist that reads the
 * flag is a separate track (tv-v4p / diagram 09 `i7`).
 *
 * Fired from `checkOutBooking`'s `noRebookDepositApplied` branch, guarded by the
 * mid-piece condition (`booking.pieceId && !markPieceComplete`). This function
 * re-checks the piece is still open as a backstop, so a finished piece or a
 * booking with nothing unfinished never raises a flag.
 *
 * Leg (b), the Sweeper open-business landing with the recent-tattoo flag, is
 * emergent: leaving the piece `in-progress` with no next session makes the client
 * surface on the Sweeper's open-business rung, where tv-25a stamps the
 * "recently tattooed" day-count. No mutation is needed here for leg (b).
 */
export function flagNoRebookMidPiece(input: FlagNoRebookInput): NoRebookFlag | undefined {
  const brain = useBrainStore.getState();
  const booking = brain.bookings[input.bookingId];
  if (!booking) return undefined;
  // Nothing is mid-piece without a linked piece: a plain no-rebook is not a leak.
  if (!booking.pieceId) return undefined;
  // Backstop: a piece already completed is not open business, so it does not flag.
  const piece = brain.pieces[booking.pieceId];
  if (piece && piece.status === 'completed') return undefined;

  const at = nowIso();
  const flag: NoRebookFlag = {
    id: `nrf-${booking.id}`,
    sourceBookingId: booking.id,
    customerId: booking.customerId,
    studioId: booking.studioId,
    pieceId: booking.pieceId,
    reason: input.reason,
    createdAt: at,
  };
  brain.upsertNoRebookFlag(flag);

  brain.appendActivity({
    id: `act-norebook-flag-${booking.id}`,
    type: 'rebook-declined',
    customerId: booking.customerId,
    studioId: booking.studioId,
    artistId: booking.artistId,
    body: `Flagged to Ops · no rebook mid-piece · reason: ${input.reason}${
      input.reasonNote ? ` (${input.reasonNote})` : ''
    }.`,
    timestamp: at,
  });

  eventBus.publish('no-rebook:flagged', {
    bookingId: booking.id,
    flagId: flag.id,
    pieceId: booking.pieceId,
  });

  return flag;
}
